import { useState } from "react";
import SEO from "@/components/SEO";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { BrandKitSection } from "@/components/onboarding/BrandKitSection";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const totalSteps = 3;

  const handleNext = () => {
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSkip = () => {
    navigate("/");
  };

  const emailTemplates = [
    {
      title: "Welcome Email",
      description: "Introduce your organization to new supporters",
      preview: "Thanks for joining the [Organization] Community!"
    },
    {
      title: "Campaign Announcement",
      description: "Launch your next fundraising campaign",
      preview: "Join us in making a difference"
    },
    {
      title: "Event Promotion",
      description: "Promote your upcoming volunteer events",
      preview: "Be part of something meaningful"
    }
  ];

  const keyActions = [
    {
      title: "Add Volunteering Events",
      description: "Create opportunities for engagement",
      icon: "🗓️"
    },
    {
      title: "Create Campaign",
      description: "Launch themed fundraising campaigns",
      icon: "📣"
    },
    {
      title: "Generate Microsite",
      description: "Share your impact with a custom page",
      icon: "🌐"
    },
    {
      title: "Customize Chatbot",
      description: "Set up AI-powered supporter engagement",
      icon: "💬"
    },
    {
      title: "Create Social Content",
      description: "Generate engaging social media posts",
      icon: "📱"
    }
  ];

  return (
    <>
      <SEO 
        title="Get Started with CauseConnect" 
        description="Set up your nonprofit profile and start making an impact with CauseConnect's AI-powered tools."
      />
      
      <div className="min-h-screen bg-gray-50">
        {currentStep === 1 ? (
          <BrandKitSection 
            onNext={handleNext}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
          />
        ) : currentStep === 2 ? (
          <div className="space-y-8 p-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Email Templates
              </h2>
              <p className="text-gray-600">
                Start with our proven templates for nonprofit communication
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6">
              {emailTemplates.map((template, idx) => (
                <div 
                  key={idx}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                >
                  <h3 className="font-semibold mb-2">{template.title}</h3>
                  <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                  <p className="text-sm text-primary">{template.preview}</p>
                </div>
              ))}
            </div>

            <div className="flex justify-end mt-6">
              <Button onClick={handleNext}>Continue</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-8 p-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Key Actions
              </h2>
              <p className="text-gray-600">
                Choose where to start with CauseConnect
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {keyActions.map((action, idx) => (
                <div 
                  key={idx}
                  className={cn(
                    "border rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer",
                    "flex items-start space-x-4"
                  )}
                  onClick={() => navigate("/signup")}
                >
                  <span className="text-2xl">{action.icon}</span>
                  <div>
                    <h3 className="font-semibold mb-1">{action.title}</h3>
                    <p className="text-sm text-gray-600">{action.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end mt-6">
              <Button onClick={() => navigate("/signup")}>Get Started</Button>
            </div>
          </div>
        )}

        {/* Progress and Navigation */}
        <div className="fixed top-0 left-0 right-0 bg-white border-b z-10 px-8 py-4">
          <div className="flex items-center justify-between mb-4">
            {currentStep > 1 && (
              <Button
                variant="ghost"
                onClick={handleBack}
                className="text-gray-600"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="text-gray-600 ml-auto"
            >
              Skip Setup
            </Button>
          </div>
          <Progress 
            value={(currentStep / totalSteps) * 100} 
            className="mb-2"
          />
        </div>

        {/* Footer */}
        <div className="fixed bottom-0 left-0 right-0 text-center text-sm text-gray-500 py-4 bg-white border-t">
          Need help? <a href="/contact" className="text-primary hover:underline">Contact Support</a>
        </div>
      </div>
    </>
  );
}
